import React, { useState } from 'react';
import styled from 'styled-components';
import { MoreVertical, Edit, Copy, Trash2, Eye } from 'lucide-react';
import { theme } from '../../styles/theme';
import { Card } from './Card';
import { formatRelativeTime, getSecretTypeIcon } from '../../utils/helpers';

const SecretCardContainer = styled(Card)`
  position: relative;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const SecretHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const SecretIcon = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  margin-right: ${({ theme }) => theme.spacing.md};
  flex-shrink: 0;
`;

const SecretInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const SecretTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SecretDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const SecretMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.md};
  padding-top: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.gray200};
`;

const SecretType = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const SecretTime = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const SecretActions = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing.md};
  right: ${({ theme }) => theme.spacing.md};
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  
  ${SecretCardContainer}:hover & {
    opacity: 1;
  }
`;

const ActionButton = styled.button`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text};
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  z-index: 10;
  min-width: 160px;
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.surfaceHover};
  }
  
  &:first-child {
    border-radius: ${({ theme }) => theme.borderRadius.md} ${({ theme }) => theme.borderRadius.md} 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 ${({ theme }) => theme.borderRadius.md} ${({ theme }) => theme.borderRadius.md};
  }
`;

const LabelsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
  margin: ${({ theme }) => theme.spacing.md} 0;
`;

const Label = styled.span`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  background-color: ${({ $color }) => $color || theme.colors.primary};
  color: white;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const secretTypes = {
  login: 'Login',
  password: 'Password',
  api_key: 'API Key',
  note: 'Note',
  card: 'Card',
  ssh: 'SSH Key',
  database: 'Database',
  other: 'Other',
};

function SecretCard({ 
  secret, 
  onSecretClick, 
  onEdit, 
  onDelete, 
  onCopyPassword, 
  onCopySecretKey,
  showActions = true,
  showLabels = true 
}) {
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  const handleActionClick = (e) => {
    e.stopPropagation();
    setShowActionsMenu(!showActionsMenu);
  };

  const handleAction = (action, e) => {
    e.stopPropagation();
    action(secret.id);
    setShowActionsMenu(false);
  };

  return (
    <SecretCardContainer onClick={() => onSecretClick(secret.id)}>
      <SecretHeader>
        <SecretIcon>
          {getSecretTypeIcon(secret.type)}
        </SecretIcon>
        <SecretInfo>
          <SecretTitle>{secret.title}</SecretTitle>
          <SecretDescription>
            {secret.description || 'No description'}
          </SecretDescription>
        </SecretInfo>
        {showActions && (
          <SecretActions>
            <ActionButton onClick={handleActionClick}>
              <MoreVertical size={16} />
            </ActionButton>
            {showActionsMenu && (
              <Dropdown>
                {onEdit && (
                  <DropdownItem onClick={(e) => handleAction(onEdit, e)}>
                    <Edit size={14} />
                    Edit
                  </DropdownItem>
                )}
                {onCopyPassword && (
                  <DropdownItem onClick={(e) => handleAction(onCopyPassword, e)}>
                    <Copy size={14} />
                    Copy Password
                  </DropdownItem>
                )}
                {onCopySecretKey && secret.secret_key && (
                  <DropdownItem onClick={(e) => handleAction(onCopySecretKey, e)}>
                    <Copy size={14} />
                    Copy Secret Key
                  </DropdownItem>
                )}
                {onDelete && (
                  <DropdownItem onClick={(e) => handleAction(onDelete, e)}>
                    <Trash2 size={14} />
                    Delete
                  </DropdownItem>
                )}
              </Dropdown>
            )}
          </SecretActions>
        )}
      </SecretHeader>
      
      {showLabels && secret.labels && secret.labels.length > 0 && (
        <LabelsContainer>
          {secret.labels.map((label) => (
            <Label key={label.id} $color={label.color}>
              {label.name}
            </Label>
          ))}
        </LabelsContainer>
      )}
      
      <SecretMeta>
        <SecretType>{secretTypes[secret.type] || 'Other'}</SecretType>
        <SecretTime>
          Updated {formatRelativeTime(secret.updated_at)}
        </SecretTime>
      </SecretMeta>
    </SecretCardContainer>
  );
}

export default SecretCard;
